/**
 * Pattern of Life Sentinel Component
 * 
 * AI-powered analysis for Pattern of Life data including:
 * - Entity behavioral profiles
 * - Recurring patterns and routes
 * - Entity correlations (co-location, co-travel)
 * - Anomalies and routine breaks
 * - Temporal activity analysis
 * - Digital patterns
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Psychology as AskIcon,
  Send as SendIcon,
  Lightbulb as PromptIcon,
  DirectionsWalk as MobilityIcon,
  People as SocialIcon,
  Warning as WarningIcon,
  Place as PlaceIcon,
  Route as RouteIcon,
  Groups as GroupsIcon,
  Smartphone as DigitalIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarIcon,
  HelpOutline as HelpIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Assessment as DashboardIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  EntitySummary,
  RecurringPattern,
  EntityCorrelation,
  PolAnomaly,
  RoutePattern,
  DigitalPattern,
  DigitalInsightsResponse,
  DigitalFingerprint,
  DailyActivityMetric,
  TemporalHeatmapData,
} from '../../services/polService';
import { POL_TAB, type POLTabId } from '../../polTabs';
import { generateSentinelChatPDF } from '@signals/app-shell/components/pdf';
import { Slideshow as SlideshowIcon } from '@mui/icons-material';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: POLAction[];
}

// POL-specific action types
export type POLActionType = 
  | 'select_entity'         // Select an entity to view details
  | 'filter_course'         // Filter by course
  | 'change_time_range'     // Change the time range
  | 'highlight_pattern'     // Highlight a pattern on map
  | 'highlight_route'       // Highlight a route on map
  | 'show_correlation'      // Show entity correlation
  | 'show_anomaly'          // Show anomaly details
  | 'compare_entities'      // Compare two entities
  | 'switch_tab'            // Switch to a specific tab
  | 'clear_highlights'      // Clear all highlights
  // Embedding-powered actions
  | 'find_similar'          // Find entities with similar behavioral profiles
  | 'show_anomaly_detection' // Show LSTM-based anomaly detection
  | 'highlight_cluster'     // Highlight a behavioral cluster
  | 'compare_behaviors';    // Compare behavioral embeddings

export interface POLAction {
  type: POLActionType;
  entityId?: string;
  entityIds?: string[];
  patternId?: string;
  routeId?: string;
  correlationId?: string;
  anomalyId?: string;
  courseId?: string;
  tabIndex?: number;
  daysBack?: number;
  label: string;
  icon?: React.ReactNode;
  autoExecute?: boolean;
  // Embedding-related data
  similarityScore?: number;
  clusterEntities?: string[];
}

interface POLSentinelProps {
  entities: EntitySummary[];
  patterns: RecurringPattern[];
  correlations: EntityCorrelation[];
  anomalies: PolAnomaly[];
  routePatterns: RoutePattern[];
  digitalPatterns: DigitalPattern[];
  digitalInsights?: DigitalInsightsResponse | null;
  /**
   * Per-entity-per-day atypicality rollup from Step 12 of the embedding pipeline.
   * Surfaces the "most interesting day" ranking and per-type anomaly counts.
   */
  topAtypicalDays?: DailyActivityMetric[];
  selectedCourse?: string;
  selectedEntity?: string | null;
  daysBack?: number;
  // Highlight a specific hour+day from the Pattern Wheel cell selection
  highlightHour?: number | null;
  highlightDay?: number | null;
  periodSummary?: {
    total_entities?: number;
    total_patterns?: number;
    total_correlations?: number;
    total_anomalies?: number;
    total_sequences?: number;
  };
  onEntitySelect?: (entityId: string | null) => void;
  onCourseSelect?: (courseId: string) => void;
  onDaysBackChange?: (days: number) => void;
  onTabChange?: (tab: POLTabId | number) => void;
  /** Same temporal_heatmap as the Activity Pattern Wheel — used for Peak hour. */
  temporalHeatmap?: TemporalHeatmapData[];
  onRouteSelect?: (route: RoutePattern | null) => void;
  onCorrelationSelect?: (correlation: EntityCorrelation | null) => void;
  onAnomalySelect?: (anomaly: PolAnomaly | null) => void;
  // Embedding-powered callbacks
  onFindSimilar?: (entityId: string) => void;
  onHighlightCluster?: (entityIds: string[]) => void;
  onCompareEntities?: (entityA: string, entityB: string) => void;
  // Export
  onExportPPTX?: (entityId: string, aiNarrative?: string) => void;
}

const POLSentinel: React.FC<POLSentinelProps> = ({
  entities,
  patterns,
  correlations,
  anomalies,
  routePatterns,
  digitalPatterns,
  digitalInsights,
  topAtypicalDays = [],
  selectedCourse = '',
  selectedEntity = null,
  daysBack = 30,
  highlightHour = null,
  highlightDay = null,
  periodSummary: _periodSummary,
  onEntitySelect,
  onCourseSelect,
  onDaysBackChange,
  onTabChange,
  temporalHeatmap = [] as TemporalHeatmapData[],
  onRouteSelect,
  onCorrelationSelect,
  onAnomalySelect,
  // Embedding callbacks
  onFindSimilar,
  onHighlightCluster,
  onCompareEntities,
  // Export
  onExportPPTX,
}) => {
  const { coursePlural } = useCourseLabels();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  /** Recharts defaults omit theme-aware text colors; labels/items stay dark on dark backgrounds without these. */
  const polChartTooltipProps = useMemo(
    () => ({
      contentStyle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: '0.7rem',
        boxShadow: theme.shadows[isDark ? 12 : 8],
      },
      labelStyle: { color: theme.palette.text.primary },
      itemStyle: { color: theme.palette.text.secondary },
    }),
    [theme, isDark],
  );

  // Chat state
  const [isLoading, setIsLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Panel sub-tab state
  const [panelView, setPanelView] = useState<'dashboard' | 'chat'>('dashboard');
  const [sentinelInfoOpen, setSentinelInfoOpen] = useState(false);

  // Prompt menu state
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const promptMenuOpen = Boolean(promptMenuAnchor);
  
  // Charts collapse state
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showCharts, setShowCharts] = useState(true);

  // Track collapsed prompt categories (all collapsed by default)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => new Set([
    'How to Use', 'Understanding Embeddings', 'Entity Analysis', 'Pattern Analysis',
    'Correlations', 'Anomalies', 'Locations', 'Location Similarity',
    'Digital Patterns', 'AI Similarity Search', 'AI Anomaly Detection',
  ]));
  const toggleCategory = useCallback((cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  // Predefined prompts for POL analysis
  const predefinedPrompts = [
    { category: 'How to Use', icon: <HelpIcon fontSize="small" />, prompts: [
      { label: 'What can you help with?', prompt: 'What can you help me with in Pattern of Life?' },
      { label: 'How to find patterns', prompt: 'How do I identify recurring patterns for an entity?' },
      { label: 'How to find correlations', prompt: 'How do I find entities that travel together or visit the same places?' },
      { label: 'How to spot anomalies', prompt: 'How do I detect anomalies or routine breaks?' },
      { label: 'Understanding the tabs', prompt: 'Explain the different tabs and what each one shows' },
      { label: 'Using the timeline', prompt: 'How do I use the timeline to analyze activity over time?' },
      { label: 'Embedding process overview', prompt: 'Give me an overview of the embedding pipeline. How does the system convert raw location data into behavioral embeddings? Walk through the full process: data ingestion, feature extraction (routine scores, mobility, social, dwell patterns, active hours), vector encoding into 64-dimensional space using pgvector, and how cosine similarity is used for search. Also explain how trajectory embeddings (64-dim) encode movement sequences and how dwell location embeddings (64-dim) capture place behavioral signatures.' },
    ]},
    { category: 'Understanding Embeddings', icon: <HelpIcon fontSize="small" />, prompts: [
      { label: 'What are behavioral embeddings?', prompt: 'Explain what behavioral embeddings are. How does the system convert entity activity patterns into mathematical vectors? What data goes into creating an embedding?' },
      { label: 'How similarity search works', prompt: 'How does the Similar Behavioral Profiles feature work? Explain what similarity scores mean, how cosine distance computes similarity, and how to interpret results.' },
      { label: 'What affects similarity?', prompt: 'What behavioral factors determine if two entities are similar? List the key metrics like routine score, mobility patterns, social activity, and dwell behavior that create the embedding vector.' },
      { label: 'LSTM anomaly detection', prompt: 'How does the LSTM-based anomaly detection work? Explain how the autoencoder learns normal patterns and flags deviations. What does reconstruction error mean?' },
      { label: 'Trajectory embeddings', prompt: 'What are trajectory embeddings for location sequences? How do they identify similar movement patterns even when specific places differ?' },
      { label: 'Embeddings vs manual analysis', prompt: 'When should I use AI similarity search instead of manually browsing entities? What are the advantages of embedding-based analysis?' },
    ]},
    { category: 'Entity Analysis', icon: <MobilityIcon fontSize="small" />, prompts: [
      { label: 'Entity Overview', prompt: 'Give me an overview of all entities — mobility scores, social scores, dwell counts, and top locations.' },
      { label: 'Most Active Entities', prompt: 'Who are the most active entities? Rank them by mobility score, unique locations visited, total dwells, and distance traveled.' },
      { label: 'Entity Comparison', prompt: 'Compare the behavioral profiles of the top entities. How do their mobility, social activity, routine scores, and location preferences differ?' },
      { label: 'Social Activity', prompt: 'Which entities have the highest social scores? Analyze their call counts, SMS activity, and unique contacts.' },
    ]},
    { category: 'Pattern Analysis', icon: <RouteIcon fontSize="small" />, prompts: [
      { label: 'Common Patterns', prompt: 'What are the most common recurring patterns across entities? Break down by pattern type and location.' },
      { label: 'Route Analysis', prompt: 'Analyze the most frequent routes — origins, destinations, usage counts, and average durations. Which routes look like commutes?' },
      { label: 'Time Patterns', prompt: 'What temporal patterns do you see? When are entities most active by hour and day of week? Are there clear weekday vs weekend differences?' },
      { label: 'Routine Adherence', prompt: 'Which entities have the most predictable routines (highest adherence rates)? Which are the least predictable?' },
    ]},
    { category: 'Correlations', icon: <GroupsIcon fontSize="small" />, prompts: [
      { label: 'Co-Location', prompt: 'Which entities frequently appear at the same locations? List the shared locations and overlap durations.' },
      { label: 'Co-Travel', prompt: 'Which entities travel together or follow similar routes? What are their shared route patterns and timing gaps?' },
      { label: 'Strong Correlations', prompt: 'Show me the strongest entity correlations (>70% strength). What types are they (co-location, co-travel) and how often do they occur?' },
    ]},
    { category: 'Anomalies', icon: <WarningIcon fontSize="small" />, prompts: [
      { label: 'All Anomalies', prompt: 'What anomalies have been detected? Break them down by detector TYPE (ml_*, time_shift, dwell_duration_anomaly, lapse_break, route_deviation, new_relationship, unusual_location, routine_break) and severity. Cite specific entities and locations.' },
      { label: 'High Severity', prompt: 'Show me only high-severity anomalies. What deviations triggered them and when were they detected?' },
      { label: 'Routine Breaks', prompt: 'Which entities have broken their usual routines? Compare their expected behavior against what actually happened.' },
      { label: 'Off-Schedule Visits', prompt: 'Have any entities arrived at recurring locations at off-schedule times? Show me the time_shift anomalies — which entity, which location, what hour vs the usual?' },
      { label: 'Unusual Dwell Durations', prompt: 'Show me dwell_duration_anomaly anomalies — entities who stayed somewhere much longer or briefer than their historical median for that location.' },
      { label: 'Missed Recurring Visits', prompt: 'Which entities have missed a recurring visit (lapse_break)? Tell me which location they have not returned to and how overdue they are.' },
      { label: 'Route Deviations', prompt: 'Show me route_deviation anomalies — trips on known recurring routes that took abnormal duration or distance. Which routes, which entities?' },
      { label: 'New Relationships', prompt: 'Have any new_relationship anomalies fired? Which entity pairs are co-locating or co-traveling for the first time, and where?' },
      { label: 'Unusual Locations', prompt: 'Show me unusual_location anomalies — entities who dwelled at places far from any of their known recurring patterns.' },
      { label: 'ML vs Statistical', prompt: 'Compare ML autoencoder anomalies (ml_*) against the statistical event-level detectors. Which detector layer is firing more for which entities?' },
    ]},
    { category: 'Forecast & Predictions', icon: <CalendarIcon fontSize="small" />, prompts: [
      { label: 'Explain the Forecast', prompt: 'Explain the Forecast tab. What do the Expected Day, LSTM forecast, Right Now panel, and Quality cell each show? How are they computed and when should I trust them?' },
      { label: 'Tomorrow\'s Expected Day', prompt: selectedEntity
        ? `What does ${selectedEntity}'s expected day look like tomorrow? Cite the row from the FORECASTS section with confidence and data_maturity, and break it down hour-by-hour (active hours, dwells, distance) using the Phase 1 baseline synthesis.`
        : 'For the entities currently visible, summarize tomorrow\'s expected_day forecast. List confidence, data_maturity, predicted active hours, and predicted dwells for each.' },
      { label: 'LSTM Next-Day View', prompt: selectedEntity
        ? `Show me ${selectedEntity}'s most recent LSTM forecast (generated_for='lstm_forecast'). What is the confidence, surprise_score, and was a Markov chain attached? Compare it against their expected_day if both are present.`
        : 'Across the visible entities, which ones have an LSTM forecast available? Rank by confidence and call out any with a surprise_score above 0.4 — those broke their predicted pattern.' },
      { label: 'Likely Next Stops', prompt: selectedEntity
        ? `Based on ${selectedEntity}'s Markov chain (or the pol_route_patterns fallback when the chain is empty), where are they most likely to go next at the current hour? Give top 3 with probabilities and the upstream evidence.`
        : 'For each entity currently visible, what are the top likely next stops at this hour? Use the Markov chain if present, otherwise the route-pattern fallback.' },
      { label: 'Forecast Quality', prompt: 'How well have our forecasts performed lately? Walk through the rolling backtest quality (composite_accuracy, visit_recall, timeline_agreement). Which entities have the most reliable predictions and which are too sparse to backtest?' },
      { label: 'Most Predictable Entity', prompt: 'Which entity is currently the most predictable based on forecast confidence + rolling backtest accuracy? Which is the least predictable and why (low data_maturity, high surprise_score, sparse Markov)?' },
      { label: 'Surprise Anomalies', prompt: 'Are there any entities whose recent actual behavior diverged sharply from what we predicted (surprise_score > 0.4)? Cross-reference with the anomaly detectors that fired on the same day.' },
    ]},
    { category: 'Daily Atypicality (Outlier Rankings)', icon: <CalendarIcon fontSize="small" />, prompts: [
      { label: 'Most Atypical Day', prompt: 'Which (entity, day) had the highest atypicality_score? Tell me which detectors fired and what the mobility was vs that entity\'s baseline.' },
      { label: 'Top 5 Most Interesting', prompt: 'Show me the top 5 most interesting (entity, day) pairs ranked by atypicality_score. For each, explain what drove the score (mobility deviation, anomaly density, or both) and which detectors fired.' },
      { label: 'Outlier Worklist', prompt: 'Build me an outlier worklist: rank atypical days by score band (High >= 0.7, Elevated >= 0.5, Moderate >= 0.3) and recommend an investigation order.' },
      { label: 'Mobility-Driven Days', prompt: 'Which atypical days were driven primarily by mobility deviation (large km/places visited) rather than anomaly detector firings? Highlight unusual travel ranges.' },
      { label: 'Detector-Driven Days', prompt: 'Which atypical days were driven primarily by anomaly detectors firing (rather than just mobility)? Group by which detector dominates.' },
      { label: 'Explain Atypicality', prompt: 'Explain the atypicality_score and the daily_activity_metrics rollup. How is the score computed, what severity bands mean, and how should I use it for outlier-day prioritization?' },
    ]},
    { category: 'Locations', icon: <PlaceIcon fontSize="small" />, prompts: [
      { label: 'Top Locations', prompt: 'What are the most visited locations across all entities? How many entities visit each and what are the typical dwell durations?' },
      { label: 'Location Patterns', prompt: 'What patterns exist in location visits? Are there locations visited at specific times or days?' },
      { label: 'Shared Locations', prompt: 'Which locations are shared by multiple entities? Do any of these shared locations correspond to known correlations?' },
    ]},
    { category: 'Location Similarity', icon: <PlaceIcon fontSize="small" />, prompts: [
      { label: 'What are dwell embeddings?', prompt: 'Explain dwell location embeddings. What behavioral patterns do they capture (visit times, duration, visitors)? How can I find similar locations?' },
      { label: 'Find similar places', prompt: 'How can I find locations that have similar behavioral patterns (same types of visitors, similar visit times, similar duration)?' },
      { label: 'Location types', prompt: 'How does the system categorize locations by type (food, retail, office, residence, etc.) in the embeddings?' },
      { label: 'Compare locations', prompt: 'What makes two dwell locations "similar" in the embedding space? What factors determine their similarity score?' },
    ]},
    { category: 'Digital Patterns', icon: <DigitalIcon fontSize="small" />, prompts: [
      { label: 'Digital Overview', prompt: 'Give me a comprehensive overview of digital activity. Summarize the digital patterns, fingerprints, and activity baseline across all entities.' },
      { label: 'App & Browsing Usage', prompt: 'What apps and websites are entities using? Break down by category (app_usage, browsing) and note occurrence counts and confidence levels.' },
      { label: 'Communication Patterns', prompt: 'Analyze communication patterns across entities. Who uses Signal, SMS, WhatsApp, or other messaging tools? What are the call and text volumes?' },
      { label: 'Digital Fingerprints', prompt: 'Describe the digital fingerprints for each entity. What makes each entity\'s digital profile distinctive? Which behavioral signals have the highest confidence?' },
      { label: 'Activity Baseline', prompt: 'Analyze the digital activity baseline heatmap. Which days and hours show the most digital activity? Are there clear patterns in when entities are digitally active?' },
      { label: 'Digital Anomalies', prompt: 'Which entities show unusual digital behavior? Analyze anomaly rates, scores, and which entities have the most anomalous days.' },
      { label: 'Digital Similarities', prompt: 'Which entities have similar digital behavioral profiles? Highlight the highest similarity pairs and what they have in common.' },
    ]},
    { category: 'AI Similarity Search', icon: <SocialIcon fontSize="small" />, prompts: [
      { label: 'Find Similar Entities', prompt: selectedEntity 
        ? `Find entities with similar behavioral profiles to ${selectedEntity}` 
        : 'Select an entity first, then I can find similar behavioral profiles' },
      { label: 'Behavioral Clusters', prompt: 'Identify behavioral clusters - groups of entities that move similarly' },
      { label: 'Who Moves Like...', prompt: selectedEntity 
        ? `Who else has movement patterns similar to ${selectedEntity}?` 
        : 'Select an entity to find who else moves like them' },
      { label: 'Compare Behaviors', prompt: 'Compare the behavioral profiles of the top entities including mobility, social, routine, and digital dimensions.' },
    ]},
    { category: 'AI Anomaly Detection', icon: <WarningIcon fontSize="small" />, prompts: [
      { label: 'Detect Unusual Behavior', prompt: selectedEntity 
        ? `Is ${selectedEntity}'s recent behavior unusual? Analyze their anomaly score and digital anomaly rate.` 
        : 'Select an entity to analyze their behavioral anomalies' },
      { label: 'Anomaly Summary', prompt: selectedEntity 
        ? `Give me an anomaly summary for ${selectedEntity} — both physical movement anomalies and digital behavior anomalies.` 
        : 'Select an entity to see their anomaly summary' },
      { label: 'Recent Deviations', prompt: 'Which entities have shown the most behavioral deviation recently? Consider both physical and digital anomalies.' },
      { label: 'Pattern Breaks', prompt: 'Who has broken their normal patterns this week? Include deviations in both location routines and digital activity.' },
    ]},
  ];

  // Theme colors
  const colors = useMemo(() => ({
    background: 'transparent', // Match page background
    cardBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    textTertiary: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
    accent: primaryColor,
    accentRgb: '59, 130, 246',
  }), [isDark, primaryColor]);

  // Analyze POL data for stats
  const polStats = useMemo(() => {
    // Entity stats
    const avgMobilityScore = entities.length > 0 
      ? entities.reduce((sum, e) => sum + (e.mobility_score || 0), 0) / entities.length 
      : 0;
    
    const avgSocialScore = entities.length > 0
      ? entities.reduce((sum, e) => sum + (e.social_score || 0), 0) / entities.length
      : 0;

    // Pattern stats
    const patternTypes: Record<string, number> = {};
    patterns.forEach(p => {
      patternTypes[p.pattern_type] = (patternTypes[p.pattern_type] || 0) + 1;
    });

    // Correlation stats
    const correlationTypes: Record<string, number> = {};
    correlations.forEach(c => {
      correlationTypes[c.correlation_type] = (correlationTypes[c.correlation_type] || 0) + 1;
    });

    // Anomaly stats by severity
    const anomalySeverity: Record<string, number> = {};
    anomalies.forEach(a => {
      anomalySeverity[a.severity] = (anomalySeverity[a.severity] || 0) + 1;
    });

    // Top entities by activity
    const topEntities = [...entities]
      .sort((a, b) => (b.mobility_score || 0) - (a.mobility_score || 0))
      .slice(0, 5);

    // Route stats
    const routeUsageCounts = routePatterns.map(r => r.usage_count || 0);
    const avgRouteUsage = routeUsageCounts.length > 0
      ? routeUsageCounts.reduce((a, b) => a + b, 0) / routeUsageCounts.length
      : 0;

    return {
      totalEntities: entities.length,
      totalPatterns: patterns.length,
      totalCorrelations: correlations.length,
      totalAnomalies: anomalies.length,
      totalRoutes: routePatterns.length,
      avgMobilityScore: avgMobilityScore.toFixed(1),
      avgSocialScore: avgSocialScore.toFixed(1),
      patternTypes,
      correlationTypes,
      anomalySeverity,
      topEntities,
      avgRouteUsage: avgRouteUsage.toFixed(1),
      highSeverityAnomalies: anomalies.filter(a => a.severity === 'high' || a.severity === 'critical').length,
    };
  }, [entities, patterns, correlations, anomalies, routePatterns]);

  // When an entity is selected on the right, narrow the left charts to just that entity
  const activeEntities = useMemo(() =>
    selectedEntity ? entities.filter(e => e.entity_id === selectedEntity) : entities,
  [entities, selectedEntity]);

  // Generate aggregated weekly heatmap from real per-(day, hour) counts
  const heatmapData = useMemo(() => {
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dowToIdx = [6, 0, 1, 2, 3, 4, 5]; // postgres DOW (0=Sun) → Mon-first index
    const timeSlots = [
      { label: '12a', hours: [0, 1, 2, 3], tooltip: '12am - 4am (Night)' },
      { label: '4a', hours: [4, 5, 6, 7], tooltip: '4am - 8am (Early Morning)' },
      { label: '8a', hours: [8, 9, 10, 11], tooltip: '8am - 12pm (Morning)' },
      { label: '12p', hours: [12, 13, 14, 15], tooltip: '12pm - 4pm (Afternoon)' },
      { label: '4p', hours: [16, 17, 18, 19], tooltip: '4pm - 8pm (Evening)' },
      { label: '8p', hours: [20, 21, 22, 23], tooltip: '8pm - 12am (Night)' },
    ];

    // Sum counts across active entities into a single grid
    const countGrid: number[][] = days.map(() => timeSlots.map(() => 0));
    activeEntities.forEach(entity => {
      (entity.hourly_heatmap || []).forEach(({ day_of_week, hour, count }) => {
        const dayIdx = dowToIdx[day_of_week];
        const slotIdx = timeSlots.findIndex(s => s.hours.includes(hour));
        if (dayIdx !== undefined && slotIdx >= 0) {
          countGrid[dayIdx][slotIdx] += count;
        }
      });
    });

    // Build highlight mask: which day+slot cells match the Pattern Wheel filter
    const highlightGrid: boolean[][] | null = (highlightHour !== null && highlightDay !== null)
      ? days.map((_, di) => {
          const pgDow = dowToIdx.indexOf(di); // convert Mon-first index back to postgres DOW
          return timeSlots.map(slot => pgDow === highlightDay && slot.hours.includes(highlightHour!));
        })
      : null;

    const allCounts = countGrid.flat();
    const maxCount = Math.max(...allCounts, 1);
    const activityGrid = countGrid.map(row =>
      row.map(count => count / maxCount)
    );

    return { days, fullDays, timeSlots, activityGrid, countGrid, highlightGrid };
  }, [activeEntities, highlightHour, highlightDay]);

  // Hourly activity data (24 bars) aggregated from active entities
  const hourlyActivity = useMemo(() => {
    const hourlyCounts: number[] = new Array(24).fill(0);
    activeEntities.forEach(entity => {
      (entity.hourly_heatmap || []).forEach(({ hour, count }) => {
        if (hour >= 0 && hour < 24) hourlyCounts[hour] += count;
      });
    });
    return hourlyCounts.map((count, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      activity: count,
      highlighted: highlightHour !== null && hour === highlightHour,
    }));
  }, [activeEntities, highlightHour]);

  // Weekly pattern data (7 bars) aggregated from active entities
  const weeklyPattern = useMemo(() => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayCounts: number[] = new Array(7).fill(0);
    activeEntities.forEach(entity => {
      (entity.hourly_heatmap || []).forEach(({ day_of_week, count }) => {
        if (day_of_week >= 0 && day_of_week < 7) dayCounts[day_of_week] += count;
      });
    });
    return dayNames.map((day, idx) => ({
      day,
      activity: dayCounts[idx],
      isWeekend: idx === 0 || idx === 6,
      highlighted: highlightDay !== null && idx === highlightDay,
    }));
  }, [activeEntities, highlightDay]);

  // Derived summary stats. Peak hour matches the Activity Pattern Wheel:
  // sum activity_count by hour across all days on temporal_heatmap.
  const derivedStats = useMemo(() => {
    const allHourlyCounts = hourlyActivity.map(h => h.activity);
    const totalPoints = allHourlyCounts.reduce((a, b) => a + b, 0);
    const wheelHourTotals = new Array(24).fill(0);
    temporalHeatmap.forEach((cell) => {
      if (cell.hour >= 0 && cell.hour < 24) {
        wheelHourTotals[cell.hour] += cell.activity_count || 0;
      }
    });
    const wheelPeakMax = Math.max(...wheelHourTotals);
    const entityPeakHour = allHourlyCounts.indexOf(Math.max(...allHourlyCounts));
    const peakHour = wheelPeakMax > 0 ? wheelHourTotals.indexOf(wheelPeakMax) : entityPeakHour;
    const totalDwells = activeEntities.reduce((sum, e) => sum + (e.total_dwells || 0), 0);
    const uniqueDays = new Set<number>();
    activeEntities.forEach(e => (e.hourly_heatmap || []).forEach(h => uniqueDays.add(h.day_of_week)));
    const activeDayCount = Math.max(uniqueDays.size, 1);
    const avgPerDay = Math.round(totalPoints / activeDayCount);

    return { totalPoints, peakHour, totalDwells, avgPerDay };
  }, [activeEntities, hourlyActivity, temporalHeatmap]);

  // Build context for AI
  const buildContext = useCallback(() => {
    // Build entity summaries
    const entitySummaries = entities.slice(0, 20).map(e => ({
      id: e.entity_id,
      mobility_score: e.mobility_score,
      social_score: e.social_score,
      total_locations: e.unique_locations,
      total_dwells: e.total_dwells,
      top_locations: e.top_locations?.slice(0, 3).map(l => l.location_name),
    }));

    // Build pattern summaries
    const patternSummaries = patterns.slice(0, 20).map(p => ({
      entity: p.entity_id,
      type: p.pattern_type,
      location: p.location_name,
      frequency: p.frequency,
      confidence: p.confidence_score,
    }));

    // Build correlation summaries
    const correlationSummaries = correlations.slice(0, 15).map(c => ({
      entity1: c.entity_a_id,
      entity2: c.entity_b_id,
      type: c.correlation_type,
      strength: c.correlation_strength,
      occurrences: c.occurrence_count,
    }));

    // Build anomaly summaries
    const anomalySummaries = anomalies.slice(0, 25).map(a => ({
      entity: a.entity_id,
      type: a.anomaly_type,
      severity: a.severity,
      expected: a.expected_behavior,
      actual: a.actual_behavior,
      detected_at: a.detected_at,
      anomaly_time: a.anomaly_time,
      deviation_score: a.deviation_score,
      location: a.actual_location?.name || a.expected_location?.name || null,
    }));

    // Tally counts per anomaly type so the AI can reason about distributions
    const anomalyTypeCounts: Record<string, number> = {};
    anomalies.forEach(a => {
      const k = (a.anomaly_type || 'unknown').toLowerCase();
      anomalyTypeCounts[k] = (anomalyTypeCounts[k] || 0) + 1;
    });

    // Build top-atypical-days summaries (Step 12 rollup output).
    // Explicit DailyActivityMetric[] cast keeps inference robust against
    // the redundant `|| []` collapsing to `never[]` under strict generics.
    const atypicalDaySource: DailyActivityMetric[] = topAtypicalDays ?? [];
    const atypicalDaySummaries = atypicalDaySource.slice(0, 15).map((d: DailyActivityMetric) => ({
      entity_id: d.entity_id,
      day: d.day,
      atypicality_score: d.atypicality_score,
      distance_traveled_km: d.distance_traveled_km,
      unique_places_visited: d.unique_places_visited,
      max_distance_from_home_km: d.max_distance_from_home_km,
      dwell_count: d.dwell_count,
      total_dwell_minutes: d.total_dwell_minutes,
      track_point_count: d.track_point_count,
      total_anomaly_count: d.total_anomaly_count,
      anomaly_breakdown: d.anomaly_breakdown,
    }));

    // Build route summaries
    const routeSummaries = routePatterns.slice(0, 15).map(r => ({
      entity: r.entity_id,
      origin: r.origin?.name,
      destination: r.destination?.name,
      usage_count: r.usage_count,
      avg_duration_seconds: r.avg_duration_seconds,
      behavioral_tags: r.behavioral_tags,
    }));

    // Build digital pattern summaries
    const digitalSummaries = digitalPatterns.slice(0, 10).map(d => ({
      entity: d.entity_id,
      category: d.category,
      name: d.name,
      description: d.description,
      occurrence_count: d.occurrence_count,
      confidence: d.confidence,
    }));

    // Build digital insights summary (fingerprints, baseline, anomalies, similarities)
    const digitalInsightsSummary = digitalInsights ? {
      summary: digitalInsights.summary,
      activityBaseline: digitalInsights.activity_baseline,
      entityProfiles: digitalInsights.entity_profiles?.slice(0, 15).map(ep => ({
        entity_id: ep.entity_id,
        anomaly_rate: ep.anomaly_rate,
        anomalous_days: ep.anomalous_days,
        total_days_scored: ep.total_days_scored,
        max_anomaly_score: ep.max_anomaly_score,
      })),
      entityFingerprints: Object.fromEntries(
        (Object.entries(digitalInsights.entity_fingerprints || {}) as [string, DigitalFingerprint[]][]).slice(0, 10).map(([entityId, fps]) => [
          entityId,
          (fps || []).slice(0, 5).map(fp => ({
            category: fp.category,
            name: fp.name,
            description: fp.description,
            confidence: fp.confidence,
          })),
        ])
      ),
      anomalyTimeline: digitalInsights.anomaly_timeline?.slice(0, 14).map(at => ({
        date: at.date,
        avg_score: at.avg_score,
        entities_scored: at.entities_scored,
        anomalies_detected: at.anomalies_detected,
      })),
      behavioralSimilarities: digitalInsights.behavioral_similarities?.slice(0, 10).map(bs => ({
        entity_a: bs.entity_a,
        entity_b: bs.entity_b,
        similarity: bs.similarity,
      })),
    } : null;

    return {
      dataType: 'pattern_of_life',
      
      // IMPORTANT: Overview section - current state of the Pattern of Life view
      overview: {
        description: `Pattern of Life analysis for ${polStats.totalEntities} entities over the last ${daysBack} days.`,
        currentFilters: {
          course: selectedCourse || ('All ' + coursePlural),
          entity: selectedEntity || 'All Entities',
          timeRange: `Last ${daysBack} days`,
        },
        dataCounts: {
          entities: polStats.totalEntities,
          patterns: polStats.totalPatterns,
          correlations: polStats.totalCorrelations,
          anomalies: polStats.totalAnomalies,
          routes: polStats.totalRoutes,
          highSeverityAnomalies: polStats.highSeverityAnomalies,
          digitalPatterns: digitalPatterns.length,
          digitalInsightsAvailable: !!digitalInsights,
        },
      },
      
      timeRange: `Last ${daysBack} days`,
      selectedCourse: selectedCourse || ('All ' + coursePlural),
      selectedEntity: selectedEntity || 'All Entities',
      
      summary: {
        totalEntities: polStats.totalEntities,
        totalPatterns: polStats.totalPatterns,
        totalCorrelations: polStats.totalCorrelations,
        totalAnomalies: polStats.totalAnomalies,
        totalRoutes: polStats.totalRoutes,
        avgMobilityScore: polStats.avgMobilityScore,
        avgSocialScore: polStats.avgSocialScore,
        highSeverityAnomalies: polStats.highSeverityAnomalies,
        avgRouteUsage: polStats.avgRouteUsage,
      },

      patternTypeBreakdown: polStats.patternTypes,
      correlationTypeBreakdown: polStats.correlationTypes,
      anomalySeverityBreakdown: polStats.anomalySeverity,
      anomalyTypeBreakdown: anomalyTypeCounts,

      topEntitiesByMobility: polStats.topEntities.map(e => ({
        id: e.entity_id,
        mobilityScore: e.mobility_score,
        socialScore: e.social_score,
      })),

      entities: entitySummaries,
      patterns: patternSummaries,
      correlations: correlationSummaries,
      anomalies: anomalySummaries,
      routes: routeSummaries,
      digitalPatterns: digitalSummaries,
      ...(digitalInsightsSummary && { digitalInsights: digitalInsightsSummary }),

      // Top Atypical Days - Step 12 daily rollup with composite atypicality_score
      topAtypicalDays: atypicalDaySummaries,
      
      // Instructions for the AI
      instructions: `When answering questions about Pattern of Life:
        - "entities" refers to devices/people being tracked
        - "patterns" are recurring behavioral patterns (visits, movements, routines)
        - "correlations" show relationships between entities (co-location, co-travel)
        - "anomalies" are deviations from normal behavior
        - "routes" are frequently traveled paths between locations
        - "topAtypicalDays" is the per-entity-per-day atypicality ranking (Step 12 rollup)
        - Use the actual counts from overview.dataCounts when asked about quantities
        - The tabs include: Overview, Entities, Patterns, Correlations, Anomalies, Forecast, Routes, Dwells, Digital
        - The Forecast tab is the user-facing surface for predictions. It shows a Right Now panel
          (current location, expected-this-hour activity, likely next stops), an Expected Day
          timeline, KPI strip with rolling backtest Quality, and an Export Brief action that
          generates a Day Ahead PowerPoint.
        
        BEHAVIORAL EMBEDDINGS (explain when asked):
        - Embeddings are 64-dimensional vectors encoding entity activity patterns
        - Created from: routine score (predictability), mobility score (travel diversity), 
          social score (communication), dwell patterns, active hours, day preferences
        - Cosine similarity compares vectors: 0.0 = different behavior, 1.0 = identical
        - High similarity (>0.7) means very similar behavior; moderate (0.4-0.7) = some overlap
        - Powered by pgvector for fast vector similarity search
        
        TRAJECTORY EMBEDDINGS:
        - Encode location sequences (movement paths) into vectors
        - Capture: sequence length, typical times/days, geographic spread
        - Find entities with similar routes even if specific places differ
        
        ANOMALY DETECTORS — TWO LAYERS:
        Layer 1: ML autoencoder anomalies (whole-day flags)
          - ml_physical_anomaly: GPS movement on this day deviated from learned baseline
          - ml_digital_anomaly: app/call/SMS pattern deviated from learned baseline
          - Both use LSTM autoencoder reconstruction error against 95th-percentile threshold
        Layer 2: Statistical event-level detectors (median + MAD per entity per location/route)
          - time_shift: arrived at a recurring location at an off-schedule hour (median+/-MAD baseline)
          - dwell_duration_anomaly: stay duration far from historical median for that location
          - lapse_break: missed a high-confidence recurring visit (no return for >> typical interval)
          - route_deviation: trip on a known route had abnormal duration or distance
          - new_relationship: first-time co-location or co-travel between two entities
          - unusual_location: dwelled at a place far from any of the entity's known patterns
          - routine_break: a strong recurring habit was suddenly not followed
        When the user asks about anomalies, prefer to cite specific anomaly TYPES from the
        anomalyTypeBreakdown rather than just counts; explain what each type means semantically.
        
        FORECAST & PREDICTIONS (Forecast tab, Phase 1-3 of the prediction stack):
        - Two row types in entity_daily_forecasts:
          * generated_for='expected_day' -> Phase 1 baseline synthesis. Fuses
            recurring_patterns + pol_route_patterns + entity_correlations +
            digital_patterns + daily_activity_metrics into a 24-hour forecast.
            No ML; only as good as the baselines under it.
          * generated_for='lstm_forecast' -> Phase 3 LSTM next-day forecaster.
            Per-entity model in embedding_models (model_type='temporal_forecaster').
            forecast_payload.markov holds a per-(hour, from_cluster) transition
            table aggregated from device dwells.
        - prediction_confidence is 0..1 calibrated. >0.7 trust, 0.5-0.7 use with caveats, <0.5 directional only.
        - data_maturity (0..1) indicates how much history fed the model. <0.3 = sparse.
        - surprise_score = 1 - cosine(predicted, actual) of the 48-dim feature vector
          (only set after target_date passes). >0.4 = the day broke expected pattern.
        - The Right Now panel polls every 60s and combines: latest dwell/track, today's
          expected_day slot for the current hour, and the Markov chain. When the chain
          is empty (sparse data) it falls back to pol_route_patterns.
        - entity_prediction_backtests has rolling accuracy:
          composite_accuracy, kpi_accuracy, visit_recall, visit_precision,
          timeline_agreement, copresence_recall.
        - When asked "what will X do tomorrow" or "predict next stops", cite the
          FORECASTS section by entity_id + date with explicit confidence values.

        TOP ATYPICAL DAYS (atypicality_score, Step 12 rollup):
        - Each row is a single (entity, day) pair with a composite score 0..1
        - Score fuses mobility deviation (how unusual today's km/places are vs. the entity's
          own historical median) + anomaly density (count of detector hits weighted by severity)
        - Use this list to answer "which entity had the most interesting day", "what stood
          out yesterday", or "rank entities by behavioral oddness"
        - Severity bands: >=0.7 High, >=0.5 Elevated, >=0.3 Moderate, <0.3 Low
        - The anomaly_breakdown on each row tells you WHY the score is high (which detectors fired)
        - Recommend the [Show Top Atypical Days] action when the user wants outlier-day prioritization`,
      
      // AI Embedding capabilities
      embeddingCapabilities: {
        available: true,
        features: [
          'behavioral_similarity: Find entities with similar movement/activity patterns (64-dim)',
          'anomaly_detection: LSTM-based detection of unusual daily patterns',
          'clustering: Identify groups of entities with similar behaviors',
          'trajectory_similarity: Find similar movement sequences (64-dim)',
          'location_similarity: Find dwell locations with similar behavioral patterns (64-dim)',
        ],
        currentEntity: selectedEntity,
        instructions: `You have AI-powered embedding features available:

          === ENTITY BEHAVIORAL EMBEDDINGS (64-dim) ===
          1. SIMILARITY SEARCH: When user asks "who behaves like X" or "find similar to X",
             suggest the find_similar action. Uses cosine similarity on 64-dimensional behavioral embeddings.
             Features: routine patterns, mobility, dwell behavior, social activity, digital patterns, routes.
          
          2. ANOMALY DETECTION: When user asks about unusual behavior or deviations,
             reference the anomaly detection panel. Uses LSTM autoencoders to learn normal patterns.
          
          3. CLUSTERS: When user asks about groups or clusters, identify entities
             with high mutual similarity scores.
          
          4. TRAJECTORY EMBEDDINGS: Movement sequence similarity (64-dim).
             Find entities with similar "A → B → C" location sequences.

          === DWELL LOCATION EMBEDDINGS (64-dim) ===
          5. LOCATION SIMILARITY: Find dwell locations with similar behavioral patterns.
             Uses 64-dimensional embeddings capturing:
             - Visit hour patterns (when people visit - 24 dimensions)
             - Day of week patterns (7 dimensions)  
             - Duration patterns (how long visits last - 6 dimensions)
             - Visitor patterns (who visits, diversity - 8 dimensions)
             - Visit frequency (regularity - 6 dimensions)
             - Place type (food, retail, office, residence, etc. - 8 dimensions)
             - Regularity metrics (consistency - 5 dimensions)
             
             Similar locations = places visited at similar times, with similar durations,
             by similar types of visitors, even if the actual visitors are different.

          When a user asks about similarity for "${selectedEntity || 'an entity'}":
          - Include specific action buttons like [Find Similar Entities] 
          - Mention the similarity percentage when available
          - For anomalies, mention how many anomalous days were detected
          
          When a user asks about location similarity:
          - Explain that similar locations share behavioral patterns (visit times, duration, visitor types)
          - Two coffee shops might be "similar" if visited at similar times by similar-profile people`,
      },
    };
  }, [entities, patterns, correlations, anomalies, routePatterns, digitalPatterns, digitalInsights,
      topAtypicalDays, daysBack, selectedCourse, selectedEntity, polStats, coursePlural]);

  // Parse AI response for actions
  const parseActionsFromResponse = useCallback((_response: string, query: string): POLAction[] => {
    const actions: POLAction[] = [];
    const q = query.toLowerCase();

    // Detect entity-specific queries
    if (q.includes('entity') || q.includes('student') || q.includes('person') || q.includes('active')) {
      if (polStats.topEntities.length > 0) {
        actions.push({
          type: 'select_entity',
          entityId: polStats.topEntities[0].entity_id,
          label: `View top entity: ${polStats.topEntities[0].entity_id}`,
          icon: <MobilityIcon fontSize="small" />,
        });
        if (polStats.topEntities.length > 1) {
          actions.push({
            type: 'select_entity',
            entityId: polStats.topEntities[1].entity_id,
            label: `View: ${polStats.topEntities[1].entity_id}`,
            icon: <MobilityIcon fontSize="small" />,
          });
        }
      }
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ENTITIES,
        label: 'Go to Entities tab',
        icon: <SocialIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect pattern queries
    if (q.includes('pattern') && !q.includes('route')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.PATTERNS,
        label: 'View Patterns tab',
        icon: <RouteIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect route queries
    if (q.includes('route') || q.includes('path') || q.includes('commute') || q.includes('travel')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ROUTES,
        label: 'View Routes tab',
        icon: <RouteIcon fontSize="small" />,
        autoExecute: true,
      });
      if (routePatterns.length > 0) {
        const topRoute = routePatterns.reduce((a, b) => (b.usage_count || 0) > (a.usage_count || 0) ? b : a, routePatterns[0]);
        actions.push({
          type: 'highlight_route',
          routeId: String(topRoute.id),
          label: `Highlight: ${topRoute.origin?.name || 'Origin'} -> ${topRoute.destination?.name || 'Dest'}`,
          icon: <RouteIcon fontSize="small" />,
        });
      }
    }

    // Detect correlation queries - provide specific highlight actions
    if (q.includes('correlation') || q.includes('co-location') || q.includes('co-travel') || q.includes('together') || q.includes('relationship')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.CORRELATIONS,
        label: 'View Correlations tab',
        icon: <GroupsIcon fontSize="small" />,
        autoExecute: true,
      });
      // Offer to highlight the strongest correlations
      const sortedCorrelations = [...correlations].sort((a, b) => (b.correlation_strength || 0) - (a.correlation_strength || 0));
      sortedCorrelations.slice(0, 2).forEach(corr => {
        actions.push({
          type: 'show_correlation',
          correlationId: `${corr.entity_a_id}-${corr.entity_b_id}`,
          label: `Highlight: ${corr.entity_a_id} <> ${corr.entity_b_id} (${Math.round((corr.correlation_strength || 0) * 100)}%)`,
          icon: <GroupsIcon fontSize="small" />,
        });
      });
    }

    // Detect anomaly queries - provide specific highlight actions
    if (q.includes('anomal') || q.includes('unusual') || q.includes('break') || q.includes('deviation') || q.includes('alert')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ANOMALIES,
        label: 'View Anomalies tab',
        icon: <WarningIcon fontSize="small" />,
        autoExecute: true,
      });
      // Offer to highlight high-severity anomalies
      const highSeverity = anomalies.filter(a => a.severity === 'high' || a.severity === 'critical');
      highSeverity.slice(0, 2).forEach(anomaly => {
        actions.push({
          type: 'show_anomaly',
          anomalyId: String(anomaly.id),
          label: `[${anomaly.severity.toUpperCase()}] ${anomaly.entity_id}: ${anomaly.anomaly_type}`,
          icon: <WarningIcon fontSize="small" />,
        });
      });
      // If no high severity, show first medium
      if (highSeverity.length === 0 && anomalies.length > 0) {
        actions.push({
          type: 'show_anomaly',
          anomalyId: String(anomalies[0].id),
          label: `[${anomalies[0].severity.toUpperCase()}] ${anomalies[0].entity_id}: ${anomalies[0].anomaly_type}`,
          icon: <WarningIcon fontSize="small" />,
        });
      }
    }

    // Detect digital pattern queries
    if (q.includes('digital') || q.includes('app') || q.includes('communication') || q.includes('sms') || q.includes('call')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.DIGITAL,
        label: 'View Digital tab',
        icon: <DigitalIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect time range change queries
    if (q.includes('last week') || q.includes('7 day')) {
      actions.push({
        type: 'change_time_range',
        daysBack: 7,
        label: 'Change to last 7 days',
        icon: <TimeIcon fontSize="small" />,
      });
    } else if (q.includes('last month') || q.includes('30 day')) {
      actions.push({
        type: 'change_time_range',
        daysBack: 30,
        label: 'Change to last 30 days',
        icon: <TimeIcon fontSize="small" />,
      });
    } else if (q.includes('last 3 month') || q.includes('90 day')) {
      actions.push({
        type: 'change_time_range',
        daysBack: 90,
        label: 'Change to last 3 months',
        icon: <TimeIcon fontSize="small" />,
      });
    }

    // Detect similarity/embedding queries
    if (q.includes('similar') || q.includes('like') || q.includes('same behavior') || 
        q.includes('behave') || q.includes('profile')) {
      if (selectedEntity) {
        actions.push({
          type: 'find_similar',
          entityId: selectedEntity,
          label: `Find entities similar to ${selectedEntity}`,
          icon: <SocialIcon fontSize="small" />,
        });
      }
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ENTITIES,
        label: 'View Similar Entities panel',
        icon: <SocialIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect anomaly detection queries (AI-powered)
    if (q.includes('lstm') || q.includes('detection') || q.includes('suspicious') ||
        (q.includes('ai') && q.includes('anomal'))) {
      if (selectedEntity) {
        actions.push({
          type: 'show_anomaly_detection',
          entityId: selectedEntity,
          label: `Analyze ${selectedEntity}'s anomaly score`,
          icon: <WarningIcon fontSize="small" />,
        });
      }
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ENTITIES,
        label: 'View AI Anomaly Detection',
        icon: <WarningIcon fontSize="small" />,
        autoExecute: true,
      });
    }

    // Detect cluster/group queries
    if (q.includes('cluster') || q.includes('behavioral group')) {
      actions.push({
        type: 'highlight_cluster',
        label: 'Highlight behavioral clusters',
        icon: <GroupsIcon fontSize="small" />,
      });
    }

    // Detect comparison queries
    if (q.includes('compare') && q.includes('behavior')) {
      if (polStats.topEntities.length >= 2) {
        actions.push({
          type: 'compare_behaviors',
          entityIds: [polStats.topEntities[0].entity_id, polStats.topEntities[1].entity_id],
          label: `Compare ${polStats.topEntities[0].entity_id} vs ${polStats.topEntities[1].entity_id}`,
          icon: <MobilityIcon fontSize="small" />,
        });
      }
    }

    // Detect overview/summary queries - offer clear highlights
    if (q.includes('overview') || q.includes('summary') || q.includes('what can') || q.includes('help')) {
      actions.push({
        type: 'switch_tab',
        tabIndex: POL_TAB.ACTIVITY,
        label: 'View Activity tab',
        icon: <CalendarIcon fontSize="small" />,
      });
      if (polStats.highSeverityAnomalies > 0) {
        actions.push({
          type: 'switch_tab',
          tabIndex: POL_TAB.ANOMALIES,
          label: `${polStats.highSeverityAnomalies} high-severity anomalies`,
          icon: <WarningIcon fontSize="small" />,
        });
      }
    }

    return actions;
  }, [polStats, selectedEntity, correlations, anomalies, routePatterns]);

  // Execute an action
  const executeAction = useCallback((action: POLAction) => {
    switch (action.type) {
      case 'select_entity':
        if (action.entityId && onEntitySelect) {
          onEntitySelect(action.entityId);
        }
        break;
      case 'switch_tab':
        if (action.tabIndex !== undefined && onTabChange) {
          onTabChange(action.tabIndex);
        }
        break;
      case 'change_time_range':
        if (action.daysBack && onDaysBackChange) {
          onDaysBackChange(action.daysBack);
        }
        break;
      case 'filter_course':
        if (action.courseId && onCourseSelect) {
          onCourseSelect(action.courseId);
        }
        break;
      case 'highlight_route':
        if (action.routeId && onRouteSelect) {
          const route = routePatterns.find(r => r.id === Number(action.routeId));
          if (route) onRouteSelect(route);
        }
        break;
      case 'show_correlation':
        if (action.correlationId && onCorrelationSelect) {
          const corr = correlations.find(c => 
            `${c.entity_a_id}-${c.entity_b_id}` === action.correlationId
          );
          if (corr) onCorrelationSelect(corr);
        }
        break;
      case 'show_anomaly':
        if (action.anomalyId && onAnomalySelect) {
          const anomaly = anomalies.find(a => a.id === Number(action.anomalyId));
          if (anomaly) onAnomalySelect(anomaly);
        }
        break;
      case 'clear_highlights':
        onRouteSelect?.(null);
        onCorrelationSelect?.(null);
        onAnomalySelect?.(null);
        break;
      // Embedding-powered actions
      case 'find_similar':
        if (action.entityId) {
          // Select the entity to trigger the Similar Entities panel
          onEntitySelect?.(action.entityId);
          // Switch to Entities tab where the panel is visible
          onTabChange?.(POL_TAB.ENTITIES);
          // Call the find similar callback if provided
          onFindSimilar?.(action.entityId);
        }
        break;
      case 'show_anomaly_detection':
        if (action.entityId) {
          // Select the entity to trigger the Anomaly Detection panel
          onEntitySelect?.(action.entityId);
          // Switch to Entities tab where both panels are visible
          onTabChange?.(POL_TAB.ENTITIES);
        }
        break;
      case 'highlight_cluster':
        if (action.clusterEntities && action.clusterEntities.length > 0) {
          onHighlightCluster?.(action.clusterEntities);
        } else if (action.entityIds && action.entityIds.length > 0) {
          onHighlightCluster?.(action.entityIds);
        }
        break;
      case 'compare_behaviors':
        if (action.entityIds && action.entityIds.length >= 2) {
          onCompareEntities?.(action.entityIds[0], action.entityIds[1]);
        }
        break;
    }
  }, [onEntitySelect, onTabChange, onDaysBackChange, onCourseSelect, onRouteSelect, 
      onCorrelationSelect, onAnomalySelect, onFindSimilar, onHighlightCluster, 
      onCompareEntities, routePatterns, correlations, anomalies]);

  // Send question to AI
  const handleSendMessage = async (queryText: string) => {
    if (!queryText.trim()) return;
    
    setQuestion('');
    setIsLoading(true);

    // Add user message
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: queryText,
      timestamp: new Date(),
    }]);

    try {
      const context = buildContext();

      const response = await fetch('/api/pol-chat/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
        },
        body: JSON.stringify({
          question: queryText,
          context,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      const assistantMessage = data.response || 'I was unable to analyze the data.';
      
      // Parse actions from response
      const actions = parseActionsFromResponse(assistantMessage, queryText);

      // Auto-execute certain actions
      actions.filter(a => a.autoExecute).forEach(action => {
        executeAction(action);
      });

      // Add assistant message with actions
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: assistantMessage,
        timestamp: new Date(),
        actions: actions.filter(a => !a.autoExecute),
      }]);

      // Scroll to bottom
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

    } catch (error: any) {
      console.error('POL Sentinel error:', error);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: `I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(question);
    }
  };

  // Clear chat
  const handleClearChat = () => {
    setChatHistory([]);
    setPanelView('dashboard');
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: colors.background,
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      {/* Sub-tab Toggle */}
      <Box sx={{ 
        p: 1, 
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}>
        <ToggleButtonGroup
          value={panelView}
          exclusive
          onChange={(_, v) => v && setPanelView(v)}
          size="small"
          sx={{
            width: '100%',
            '& .MuiToggleButton-root': {
              flex: 1,
              fontSize: '0.7rem',
              py: 0.5,
              fontWeight: 600,
              textTransform: 'none',
              color: colors.textSecondary,
              borderColor: colors.border,
              '&.Mui-selected': {
                bgcolor: alpha(colors.accent, 0.12),
                color: colors.accent,
                borderColor: alpha(colors.accent, 0.4),
                '&:hover': {
                  bgcolor: alpha(colors.accent, 0.18),
                },
              },
              '&:hover': {
                bgcolor: alpha(colors.accent, 0.05),
              },
            }
          }}
        >
          <ToggleButton value="dashboard">
            <DashboardIcon sx={{ fontSize: 15, mr: 0.5 }} />
            Dashboard
          </ToggleButton>
          <ToggleButton value="chat">
            <AskIcon sx={{ fontSize: 15, mr: 0.5 }} />
            Ask Sentinel
            {chatHistory.length > 0 && (
              <Box component="span" sx={{ 
                ml: 0.5, 
                px: 0.5, 
                py: 0.1,
                fontSize: '0.55rem', 
                borderRadius: 1, 
                bgcolor: alpha(colors.accent, 0.2),
                color: colors.accent,
                lineHeight: 1.2,
              }}>
                {chatHistory.length}
              </Box>
            )}
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="What is Sentinel?">
          <IconButton size="small" onClick={() => setSentinelInfoOpen(true)} sx={{ p: 0.25, flexShrink: 0 }}>
            <InfoIcon sx={{ fontSize: 16, color: colors.textSecondary, opacity: 0.6, '&:hover': { opacity: 1 } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ====== DASHBOARD VIEW ====== */}
      {panelView === 'dashboard' && (
        <Box sx={{ flex: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '2px' } }}>
          {/* Stats Grid - covers all tabs */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1px',
            m: 1.5, 
            mb: 1,
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: colors.border,
            border: `1px solid ${colors.border}`,
          }}>
            {([
              { value: polStats.totalEntities, label: 'Entities', color: colors.accent, icon: <SocialIcon sx={{ fontSize: 11 }} />, tabId: 'entities' as const },
              { value: polStats.totalPatterns, label: 'Patterns', color: '#4caf50', icon: <RouteIcon sx={{ fontSize: 11 }} />, tabId: 'patterns' as const },
              { value: polStats.totalCorrelations, label: 'Correlations', color: '#2196f3', icon: <GroupsIcon sx={{ fontSize: 11 }} />, tabId: 'correlations' as const },
              { value: polStats.totalAnomalies, label: 'Anomalies', color: polStats.highSeverityAnomalies > 0 ? '#f44336' : '#ff9800', icon: <WarningIcon sx={{ fontSize: 11 }} />, tabId: 'anomalies' as const },
              { value: polStats.totalRoutes, label: 'Routes', color: '#ab47bc', icon: <RouteIcon sx={{ fontSize: 11 }} />, tabId: 'routes' as const },
              { value: derivedStats.totalDwells.toLocaleString(), label: 'Dwells', color: '#26a69a', icon: <PlaceIcon sx={{ fontSize: 11 }} />, tabId: 'dwells' as const },
              { value: digitalPatterns.length, label: 'Digital', color: '#7e57c2', icon: <DigitalIcon sx={{ fontSize: 11 }} />, tabId: 'digital' as const },
              { value: derivedStats.peakHour !== undefined ? `${String(derivedStats.peakHour).padStart(2, '0')}:00` : '--', label: 'Peak', color: '#f59e0b', icon: <TimeIcon sx={{ fontSize: 11 }} />, tabId: 'activity' as const },
            ]).map((stat, idx) => (
              <Box 
                key={idx}
                onClick={() => onTabChange?.(stat.tabId)}
                sx={{ 
                  textAlign: 'center', 
                  py: 0.75,
                  px: 0.25,
                  bgcolor: alpha(colors.accent, 0.02),
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  '&:hover': { 
                    bgcolor: alpha(stat.color, 0.1),
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, color: stat.color, mb: 0.25 }}>
                  {stat.icon}
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', lineHeight: 1, color: stat.color }}>
                    {stat.value}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.45rem', color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Collapsible Activity Heatmap */}
          <Box sx={{ px: 1.5, pb: showHeatmap ? 1 : 0 }}>
            <Box 
              onClick={() => setShowHeatmap(!showHeatmap)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 0.5,
                '&:hover': { opacity: 0.8 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 14, color: colors.accent }} />
                <Tooltip 
                  title="Aggregated activity counts across all entities. Intensity is relative to the peak time block."
                  arrow
                  placement="top"
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: colors.accent,
                      cursor: 'help',
                    }}
                  >
                    Activity Heatmap
                  </Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: colors.textTertiary }}>
                  Hour x Day
                </Typography>
              </Box>
              <IconButton size="small" sx={{ p: 0 }}>
                {showHeatmap ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
            
            <Collapse in={showHeatmap}>
              <Box sx={{ 
                mt: 0.5, 
                p: 1, 
                borderRadius: 1.5,
                bgcolor: alpha(colors.accent, 0.03), 
                border: `1px solid ${colors.border}`,
              }}>
                {/* Legend row */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: colors.textTertiary }}>Less</Typography>
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: alpha('#3b82f6', 0.2) }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: alpha('#3b82f6', 0.45) }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: alpha('#f59e0b', 0.55) }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: alpha('#f59e0b', 0.9) }} />
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: colors.textTertiary }}>More</Typography>
                  </Stack>
                </Box>

                <Stack direction="row" spacing={0.5}>
                  {/* Y-axis labels (time slots) */}
                  <Stack spacing={0.25} justifyContent="space-between" sx={{ pt: '2px', pb: '2px' }}>
                    {heatmapData.timeSlots.map((slot, slotIdx) => (
                      <Typography 
                        key={slotIdx}
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.5rem', 
                          color: colors.textSecondary,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 1,
                          fontFamily: 'monospace',
                        }}
                      >
                        {slot.label}
                      </Typography>
                    ))}
                  </Stack>
                  {/* Heatmap grid */}
                  <Stack direction="row" spacing={0.5} sx={{ flex: 1, justifyContent: 'center' }}>
                    {heatmapData.days.map((day, dayIdx) => (
                      <Stack key={dayIdx} spacing={0.25} alignItems="center">
                        <Typography variant="caption" sx={{ 
                          fontSize: '0.6rem', color: colors.textSecondary, fontWeight: 'bold', mb: 0.25,
                          opacity: (dayIdx === 5 || dayIdx === 6) ? 0.6 : 1,
                        }}>
                          {day}
                        </Typography>
                        {heatmapData.timeSlots.map((slot, slotIdx) => {
                          const intensity = heatmapData.activityGrid[dayIdx][slotIdx];
                          const rawCount = heatmapData.countGrid[dayIdx][slotIdx];
                          const isHL = heatmapData.highlightGrid?.[dayIdx]?.[slotIdx] ?? false;
                          return (
                            <Tooltip 
                              key={slotIdx} 
                              title={`${heatmapData.fullDays[dayIdx]} ${slot.tooltip} — ${rawCount.toLocaleString()} event${rawCount !== 1 ? 's' : ''}`} 
                              arrow 
                              placement="top"
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: 0.5,
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  bgcolor: intensity > 0.65
                                    ? alpha('#f59e0b', 0.9)
                                    : intensity > 0.35
                                      ? alpha('#f59e0b', 0.55)
                                      : intensity > 0.1
                                        ? alpha('#3b82f6', 0.45)
                                        : intensity > 0
                                          ? alpha('#3b82f6', 0.2)
                                          : alpha(colors.text, 0.06),
                                  outline: isHL ? '2px solid #ffffff' : 'none',
                                  outlineOffset: '1px',
                                  transform: isHL ? 'scale(1.2)' : 'none',
                                  zIndex: isHL ? 2 : 'auto',
                                  '&:hover': {
                                    transform: 'scale(1.3)',
                                    boxShadow: `0 0 10px ${alpha(colors.accent, 0.5)}`,
                                    zIndex: 1,
                                  }
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Collapse>
          </Box>

          {/* Collapsible Mini Charts */}
          <Box sx={{ px: 1.5, pb: showCharts ? 1.5 : 0 }}>
            <Box 
              onClick={() => setShowCharts(!showCharts)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 0.5,
                '&:hover': { opacity: 0.8 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 14, color: colors.accent }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: colors.accent }}>
                  Activity Charts
                </Typography>
              </Box>
              <IconButton size="small" sx={{ p: 0 }}>
                {showCharts ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={showCharts}>
              {/* Hourly Activity Bar Chart */}
              {hourlyActivity.some(h => h.activity > 0) && (
                <Box sx={{ 
                  mt: 0.5,
                  mb: 1,
                  p: 1, 
                  borderRadius: 1.5,
                  bgcolor: alpha(colors.accent, 0.03), 
                  border: `1px solid ${colors.border}`,
                }}>
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 'bold', 
                    color: colors.accent, 
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                    Hourly Activity
                  </Typography>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={hourlyActivity} margin={{ top: 2, right: 2, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="polHourlyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.accent} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={colors.accent} stopOpacity={0.15}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(colors.text, 0.06)} vertical={false} />
                      <XAxis 
                        dataKey="hour" 
                        tick={{ fill: colors.textSecondary, fontSize: 7 }}
                        interval={5}
                        tickLine={false}
                        axisLine={{ stroke: alpha(colors.text, 0.1) }}
                      />
                      <YAxis 
                        tick={{ fill: colors.textSecondary, fontSize: 7 }}
                        width={28}
                        tickLine={false}
                        axisLine={false}
                      />
                      <RechartsTooltip
                        {...polChartTooltipProps}
                        formatter={(value: number) => [value.toLocaleString(), 'Events']}
                        labelFormatter={(label) => `Time: ${label}`}
                      />
                      <Bar dataKey="activity" radius={[2, 2, 0, 0]}>
                        {hourlyActivity.map((entry, idx) => (
                          <Cell
                            key={idx}
                            fill={entry.highlighted ? '#ffffff' : `url(#polHourlyGradient)`}
                            opacity={highlightHour !== null ? (entry.highlighted ? 1 : 0.25) : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Weekly Pattern Bar Chart */}
              {weeklyPattern.some(d => d.activity > 0) && (
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5,
                  bgcolor: alpha(colors.accent, 0.03), 
                  border: `1px solid ${colors.border}`,
                }}>
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 'bold', 
                    color: colors.accent, 
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                    Weekly Pattern
                  </Typography>
                  <ResponsiveContainer width="100%" height={90}>
                    <BarChart data={weeklyPattern} margin={{ top: 2, right: 2, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="polWeekdayGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.accent} stopOpacity={0.9}/>
                          <stop offset="95%" stopColor={colors.accent} stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="polWeekendGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(colors.text, 0.06)} vertical={false} />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fill: colors.textSecondary, fontSize: 8 }}
                        tickLine={false}
                        axisLine={{ stroke: alpha(colors.text, 0.1) }}
                      />
                      <YAxis 
                        tick={{ fill: colors.textSecondary, fontSize: 7 }}
                        width={28}
                        tickLine={false}
                        axisLine={false}
                      />
                      <RechartsTooltip
                        {...polChartTooltipProps}
                        formatter={(value: number) => [value.toLocaleString(), 'Events']}
                      />
                      <Bar dataKey="activity" radius={[3, 3, 0, 0]}>
                        {weeklyPattern.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={entry.highlighted ? '#ffffff' : entry.isWeekend ? 'url(#polWeekendGradient)' : 'url(#polWeekdayGradient)'}
                            opacity={highlightDay !== null ? (entry.highlighted ? 1 : 0.25) : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Collapse>
          </Box>
        </Box>
      )}

      {/* ====== ASK SENTINEL VIEW ====== */}
      {panelView === 'chat' && (
        <>
          {/* Chat Header Actions */}
          {chatHistory.length > 0 && (
            <Box sx={{ 
              px: 1.5, py: 0.5, 
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end',
              gap: 0.5,
            }}>
              <Tooltip title="Export to PDF">
                <IconButton 
                  size="small" 
                  onClick={async () => {
                    await generateSentinelChatPDF(
                      {
                        title: 'Pattern of Life Report',
                        subtitle: 'Behavioral Analysis Intelligence',
                        messages: chatHistory.map(m => ({
                          role: m.role,
                          content: m.content,
                          timestamp: m.timestamp,
                        })),
                        additionalContext: `Analysis Context: ${entities.length} entities, ${patterns.length} patterns, ${correlations.length} correlations, ${anomalies.length} anomalies`,
                        classification: 'Controlled Unclassified Information (CUI)',
                      },
                      'Pattern_of_Life_Report.pdf'
                    );
                  }}
                >
                  <PdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {selectedEntity && onExportPPTX && (
                <Tooltip title={`Export ${selectedEntity} POL to PowerPoint`}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const lastAiMsg = [...chatHistory].reverse().find(m => m.role === 'assistant');
                      onExportPPTX(selectedEntity, lastAiMsg?.content);
                    }}
                  >
                    <SlideshowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Clear chat">
                <IconButton size="small" onClick={handleClearChat}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Chat History */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 1.5,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { 
              background: 'rgba(255,255,255,0.1)', 
              borderRadius: '2px' 
            },
          }}>
            {chatHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AskIcon sx={{ fontSize: 40, color: alpha(colors.accent, 0.3), mb: 1 }} />
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                  Ask Sentinel
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ask about patterns, entities, correlations, anomalies, or routes...
                </Typography>
              </Box>
            ) : (
              <Box>
                {chatHistory.map((msg, idx) => (
              <Box 
                key={idx} 
                sx={{ 
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: msg.role === 'user' ? colors.cardBg : 'transparent',
                  borderLeft: msg.role === 'assistant' ? `2px solid ${colors.accent}` : 'none',
                }}
              >
                <Typography 
                  variant="caption" 
                  fontWeight="bold" 
                  sx={{ color: msg.role === 'user' ? colors.text : colors.accent }}
                >
                  {msg.role === 'user' ? 'You' : 'Ask Sentinel'}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5, 
                    color: colors.text,
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                  }}
                >
                  {msg.content}
                </Typography>
                
                {/* Action buttons */}
                {msg.actions && msg.actions.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {msg.actions.map((action, aIdx) => (
                      <Button
                        key={aIdx}
                        size="small"
                        variant="outlined"
                        startIcon={action.icon as any}
                        onClick={() => executeAction(action)}
                        sx={{ 
                          fontSize: '0.7rem', 
                          textTransform: 'none',
                          py: 0.25,
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>
        )}
          </Box>

          {/* Input Area */}
          <Box sx={{ 
            p: 1.5, 
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}>
            <Tooltip title="Suggested prompts">
              <IconButton 
                size="small" 
                onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
              >
                <PromptIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about patterns, entities, anomalies..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.85rem',
                }
              }}
            />
            
            <IconButton 
              color="primary" 
              onClick={() => handleSendMessage(question)}
              disabled={!question.trim() || isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </>
      )}

      {/* Prompt Menu */}
      <Menu
        anchorEl={promptMenuAnchor}
        open={promptMenuOpen}
        onClose={() => setPromptMenuAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: { maxHeight: 400, width: 280 }
        }}
      >
        {predefinedPrompts.map((category, idx) => {
          const isCollapsed = collapsedCategories.has(category.category);
          return [
            <MenuItem
              key={`cat-${idx}`}
              onClick={() => toggleCategory(category.category)}
              sx={{ opacity: 1, py: 0.5, minHeight: 32 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>{category.icon}</ListItemIcon>
              <ListItemText
                primary={category.category}
                primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }}
              />
              {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} /> : <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.5 }} />}
            </MenuItem>,
            ...(!isCollapsed ? category.prompts.map((prompt, pIdx) => (
              <MenuItem
                key={`prompt-${idx}-${pIdx}`}
                onClick={() => {
                  setPromptMenuAnchor(null);
                  setPanelView('chat');
                  handleSendMessage(prompt.prompt);
                }}
                sx={{ pl: 4 }}
              >
                <ListItemText
                  primary={prompt.label}
                  primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }}
                />
              </MenuItem>
            )) : []),
          ];
        }).flat()}
      </Menu>

      {/* Sentinel Info Dialog */}
      <Dialog open={sentinelInfoOpen} onClose={() => setSentinelInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            About Sentinel
          </Box>
          <IconButton size="small" onClick={() => setSentinelInfoOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph sx={{ lineHeight: 1.7 }}>
            <strong>Dashboard</strong> — Aggregated statistics and visualizations for the selected entities. Shows activity counts, dwell totals, distance traveled, and temporal heatmaps summarizing when and how much activity occurs across the dataset.
          </Typography>
          <Typography variant="body2" paragraph sx={{ lineHeight: 1.7 }}>
            <strong>Ask Sentinel</strong> — AI-powered conversational analysis. Ask natural-language questions about entity behavior, patterns, anomalies, correlations, or routes. Sentinel has access to all loaded PoL data (entity profiles, patterns, correlations, anomalies, route patterns, and digital behavior) and will synthesize answers with specific evidence from the data.
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Use the suggested prompts (lightbulb icon) for common analysis questions, or type your own. Sentinel can compare entities, explain anomalies, summarize behavioral trends, and generate narrative reports.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSentinelInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POLSentinel;

